/**
 * Full Profile View for Searched Users - Beautiful Redesign
 * Displays comprehensive profile information in an organized, attractive layout
 * Left side: Basic profile info card, Right side: Photo gallery
 * Below: Detailed sections in beautiful cards
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { expressInterest, checkExistingInterest } from '@/services/profileInterest';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, Calendar, MapPin, Briefcase, Phone, Mail, Home, Users, FileText, Video, 
  Heart, MessageCircle, Star, CheckCircle, User, Cake, Scale, GraduationCap, 
  Building, Eye, ChevronLeft, ChevronRight, X, Download, Share2, 
  Activity, Shield, Award, Globe, Clock, UserCheck, HeartHandshake, AlertCircle, ZoomIn, Camera
} from 'lucide-react';

import ChatModal from '@/components/ChatModal';
import { getApiBaseUrl, getMediaUrl } from '@/config/api';

type BasicInfo = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  gender: string;
  dob?: string;
  profession?: string;
  city?: string;
  state?: string;
  country?: string;
  height?: number;
  weight?: number;
  religion?: string;
  caste?: string;
  motherTongue?: string;
  maritalStatus?: string;
  education?: string;
  isVerified?: boolean;
  isPremium?: boolean;
  isOnline?: boolean;
  lastActive?: string;
  createdAt?: string;
  overallStatus?: string;
  PaymentDone?: number;
  ProfileValidity?: string;
};

type PersonalDetails = {
  aboutMe?: string;
  bodyType?: string;
  complexion?: string;
  physicalStatus?: string;
  bloodGroup?: string;
  diet?: string;
  smoke?: string;
  drink?: string;
  hobbies?: string;
  interests?: string;
  education?: string;
  educationField?: string;
  workExperience?: string;
  annualIncome?: string;
  employedIn?: string;
  occupation?: string;
  jobLocation?: string;
  organization?: string;
  workingSince?: string;
  // Astrological fields
  subCaste?: string;
  gotra?: string;
  manglik?: string;
  horoscopeMatch?: string;
  birthTime?: string;
  birthPlace?: string;
  rashi?: string;
  nakshatra?: string;
  charan?: string;
  gan?: string;
  nadi?: string;
};

type FamilyDetails = {
  fatherName?: string;
  fatherOccupation?: string;
  fatherEducation?: string;
  fatherAlive?: boolean;
  motherName?: string;
  motherOccupation?: string;
  motherEducation?: string;
  motherAlive?: boolean;
  brothersCount?: number;
  sistersCount?: number;
  marriedBrothers?: number;
  marriedSisters?: number;
  unmarriedBrothers?: number;
  unmarriedSisters?: number;
  familyType?: string;
  familyStatus?: string;
  familyValues?: string;
  familyIncome?: string;
  aboutFamily?: string;
  nativePlace?: string;
  ancestralOrigin?: string;
  familyLivingIn?: string;
  familyProperty?: string;
  familyBusiness?: string;
  noOfFamilyMembers?: number;
};

type AddressDetails = {
  buildingNo?: string;
  buildingName?: string;
  areaName?: string;
  street?: string;
  landmark?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  residence?: string;
  addressType?: string;
};

type PartnerExpectations = {
  id?: number;
  userId?: string;
  ageMin?: number;
  ageMax?: number;
  heightMin?: number;
  heightMax?: number;
  maritalStatus?: string;
  religion?: string;
  caste?: string;
  motherTongue?: string;
  education?: string;
  profession?: string;
  annualIncome?: string;
  location?: string;
  country?: string;
  diet?: string;
  smoke?: string;
  drink?: string;
  manglik?: string;
  horoscopeMatch?: string;
  physicalStatus?: string;
  familyValues?: string;
  familyType?: string;
  familyStatus?: string;
  aboutPartner?: string;
  createdAt?: string;
  updatedAt?: string;
};

type Photo = {
  id: number;
  userId: string;
  photoFileName: string;
  photoUrl: string;
  photoSize: number;
  photoFormat: string;
  isProfileDP: boolean;
  approvalStatus?: number;
  uploadDate?: string;
};

type Document = {
  id: number;
  userId: string;
  documentType: string;
  documentUrl: string;
  documentFileName: string;
  documentSize: number;
  documentFormat: string;
  status?: string;
  createdAt?: string;
};

type Video = {
  id: number;
  userId: string;
  videoTitle?: string;
  videoUrl: string;
  videoFileName: string;
  videoSize: number;
  videoFormat: string;
  videoDuration?: number;
  status?: string;
  createdAt?: string;
};

type CompleteProfile = {
  basic: BasicInfo;
  personal: PersonalDetails;
  family: FamilyDetails;
  address: AddressDetails;
  partnerExpectations: PartnerExpectations;
  photos: Photo[];
  documents: Document[];
  videos: Video[];
};

const API_BASE = getApiBaseUrl();
const API = `${API_BASE}/api`;

export default function UserSearchProfile() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  
  const [profile, setProfile] = useState<CompleteProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [showFullPhoto, setShowFullPhoto] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string>('personal');

  /**
   * Chat state
   * - chatOpen: controls modal visibility
   * - chatConversationWith: minimal target info (id + name)
   */
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConversationWith, setChatConversationWith] = useState<{ id: string; name: string } | null>(null);

  /**
   * interestExpressed
   *
   * @description Tracks whether the logged-in user has successfully expressed interest
   * in this profile during the current session. When true, the main action button will
   * show "Expressed Interest" and be disabled to avoid duplicate clicks.
   */
  const [interestExpressed, setInterestExpressed] = useState<boolean>(false);

  // Edit mode states
  const [editAboutMe, setEditAboutMe] = useState(false);
  const [editAboutFamily, setEditAboutFamily] = useState(false);
  const [editAboutPartner, setEditAboutPartner] = useState(false);
  const [aboutMeValue, setAboutMeValue] = useState('');
  const [aboutFamilyValue, setAboutFamilyValue] = useState('');
  const [aboutPartnerValue, setAboutPartnerValue] = useState('');

  // Payment plan states
  const [selectedPlan, setSelectedPlan] = useState<'3' | '6' | '12'>('3');
  const [profileValidity, setProfileValidity] = useState<Date>(new Date());
  const [updatingPayment, setUpdatingPayment] = useState(false);

  // Handle plan selection
  const handlePlanChange = (plan: '3' | '6' | '12') => {
    setSelectedPlan(plan);
    const currentDate = new Date();
    const months = parseInt(plan);
    const newDate = new Date(currentDate.setMonth(currentDate.getMonth() + months));
    setProfileValidity(newDate);
  };

  // Handle payment update
  const handlePaymentUpdate = async () => {
    try {
      setUpdatingPayment(true);
      
      if (!userId) {
        toast.error('User ID is missing');
        return;
      }

      const response = await fetch(`${API}/admin/update-payment-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          paymentDone: 1,
          profileValidity: profileValidity.toISOString().split('T')[0], // Format as YYYY-MM-DD
          plan: `${selectedPlan} Months`
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Payment updated successfully! Plan: ${selectedPlan} months`);
        // Optionally refresh profile data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to update payment status');
      }
    } catch (error) {
      console.error('Payment update error:', error);
      toast.error('Failed to update payment status');
    } finally {
      setUpdatingPayment(false);
    }
  };



  /** Fetch complete profile using NEW dedicated UserSearchProfile endpoint */
  async function fetchProfile() {
    try {
      if (!userId) {
        throw new Error('Missing userId');
      }
      setLoading(true);
      
      try {
        const profileRes = await fetch(`${API}/usersearchprofile/${encodeURIComponent(userId)}`);
        
        if (!profileRes.ok) {
          const errorText = await profileRes.text();
          throw new Error(`HTTP ${profileRes.status}: ${profileRes.statusText}`);
        }
        
        const profileResult = await profileRes.json();
        
        if (!profileResult.success) {
          throw new Error(profileResult.message || 'API returned failure');
        }
        
        if (!profileResult.data) {
          throw new Error('No profile data received');
        }
        
        const completeProfile = profileResult.data;
        
        // Set complete profile data directly
        setProfile({
          basic: completeProfile.basic,
          personal: completeProfile.personal,
          family: completeProfile.family,
          address: completeProfile.address,
          partnerExpectations: completeProfile.partnerExpectations,
          photos: completeProfile.photos,
          documents: completeProfile.documents,
          videos: completeProfile.videos || []
        });
        
        // Initialize edit values
        setAboutMeValue(completeProfile.personal?.aboutMe || '');
        setAboutFamilyValue(completeProfile.family?.aboutFamily || '');
        setAboutPartnerValue(completeProfile.partnerExpectations?.aboutPartner || '');
        
        // Initialize payment validity date from profile or use current date
        if (completeProfile.basic?.ProfileValidity) {
          setProfileValidity(new Date(completeProfile.basic.ProfileValidity));
        } else {
          setProfileValidity(new Date());
        }
        
      } catch (apiError) {
        // Set error state
        setProfile({
          basic: {
            userId: userId || '',
            firstName: 'API Error',
            lastName: 'Service Unavailable',
            email: '',
            mobileNo: '',
            profession: 'Please try again',
            dob: '1995-01-01',
            gender: '—',
            height: 170,
            weight: 65,
            religion: '—',
            caste: '—',
            motherTongue: '—',
            maritalStatus: '—',
            overallStatus: 'api_error'
          },
          personal: { aboutMe: 'Unable to load profile data. Please refresh the page.' },
          family: {},
          address: {},
          partnerExpectations: {},
          photos: [],
          documents: [],
          videos: []
        });
      }
    } catch (err) {
      // This should only happen due to network errors
      setProfile({
        basic: {
          userId: userId || '',
          firstName: 'Network Error',
          lastName: 'Connection Failed',
          email: '',
          mobileNo: '',
          profession: 'Check Internet',
          dob: '1995-01-01',
          gender: '—',
          height: 170,
          weight: 65,
          religion: '—',
          caste: '—',
          motherTongue: '—',
          maritalStatus: '—',
          overallStatus: 'network_error'
        },
        personal: { aboutMe: 'Network error occurred while fetching profile data.' },
        family: {},
        address: {},
        partnerExpectations: {},
        photos: [],
        documents: [],
        videos: []
      });
    } finally {
      setLoading(false);
    }
  }

  // Handle About Me save
  async function handleSaveAboutMe() {
    try {
      const response = await fetch(`${API}/profile-sections/personal/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aboutMe: aboutMeValue })
      });
      
      if (response.ok) {
        toast.success('About Me updated successfully');
        setEditAboutMe(false);
        await fetchProfile();
      } else {
        toast.error('Failed to update About Me');
      }
    } catch (error) {
      toast.error('Error updating About Me');
    }
  }

  // Handle About Family save
  async function handleSaveAboutFamily() {
    try {
      const response = await fetch(`${API}/profile-sections/family/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aboutFamily: aboutFamilyValue })
      });
      
      if (response.ok) {
        toast.success('About Family updated successfully');
        setEditAboutFamily(false);
        await fetchProfile();
      } else {
        toast.error('Failed to update About Family');
      }
    } catch (error) {
      toast.error('Error updating About Family');
    }
  }

  // Handle About Partner save
  async function handleSaveAboutPartner() {
    try {
      const response = await fetch(`${API}/partner-expectations/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aboutPartner: aboutPartnerValue })
      });
      
      if (response.ok) {
        toast.success('About Partner updated successfully');
        setEditAboutPartner(false);
        await fetchProfile();
      } else {
        toast.error('Failed to update About Partner');
      }
    } catch (error) {
      toast.error('Error updating About Partner');
    }
  }

  // Handle Verify button click
  async function handleVerifyUser() {
    try {
      const response = await fetch(`${API}/users/${userId}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          overallStatus: 'approved', 
          isActive: 1,
          isVerified: 1 
        })
      });
      
      if (response.ok) {
        toast.success('User verified successfully');
        await fetchProfile();
      } else {
        toast.error('Failed to verify user');
      }
    } catch (error) {
      toast.error('Error verifying user');
    }
  }

  /** Fetch on mount */
  /**
   * On mount when userId changes:
   * - Fetch profile
   * - Check if logged-in user has already expressed interest for this profile and lock the button if so
   */
  useEffect(() => {
    if (!userId) return;

    (async () => {
      await fetchProfile();

      try {
        const stored = localStorage.getItem('userData');
        if (!stored) return;
        const user = JSON.parse(stored || '{}');
        const loggedInUserId = user.userId;
        if (!loggedInUserId) return;

        // Check whether an interest record already exists (pending/previous)
        const exists = await checkExistingInterest(loggedInUserId, userId);
        if (exists) {
          setInterestExpressed(true);
        }
      } catch (err) {
        // ignore check errors — do not block rendering
        // eslint-disable-next-line no-console
        console.warn('Failed to check existing interest on load:', err);
      }
    })();
  }, [userId]);

  /** Approve photo - Update approvalStatus to 1 */
  async function handleApprovePhoto(photoId: number) {
    try {
      const response = await fetch(`${API}/photos/approve/${photoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Photo approved successfully');
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to approve photo');
      }
    } catch (error) {
      console.error('Error approving photo:', error);
      toast.error('Error approving photo. Please try again.');
    }
  }

  /** Delete photo - Remove from database and physical location */
  async function handleDeletePhoto(photoId: number, photoFileName: string) {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this photo? This action cannot be undone.');
      if (!confirmed) return;

      const response = await fetch(`${API}/photos/delete/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photoFileName }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Photo deleted successfully');
        // Reset active photo index if needed
        if (activePhotoIndex >= (profile?.photos.length || 1) - 1) {
          setActivePhotoIndex(0);
        }
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Error deleting photo. Please try again.');
    }
  }

  /** Approve document - Update status to 'Approved' */
  async function handleApproveDocument(documentId: number) {
    try {
      const response = await fetch(`${API}/user-documents/approve/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Document approved successfully');
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to approve document');
      }
    } catch (error) {
      console.error('Error approving document:', error);
      toast.error('Error approving document. Please try again.');
    }
  }

  /** Delete document - Remove from database and physical location */
  async function handleDeleteDocument(documentId: number, documentFileName: string) {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this document? This action cannot be undone.');
      if (!confirmed) return;

      const response = await fetch(`${API}/user-documents/delete/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentFileName }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Document deleted successfully');
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error deleting document. Please try again.');
    }
  }

  /** Approve video - Update status to 'Approved' */
  async function handleApproveVideo(videoId: number) {
    try {
      const response = await fetch(`${API}/user-videos/approve/${videoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Video approved successfully');
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to approve video');
      }
    } catch (error) {
      console.error('Error approving video:', error);
      toast.error('Error approving video. Please try again.');
    }
  }

  /** Delete video - Remove from database and physical location */
  async function handleDeleteVideo(videoId: number, videoFileName: string) {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this video? This action cannot be undone.');
      if (!confirmed) return;

      const response = await fetch(`${API}/user-videos/delete/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoFileName }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Video deleted successfully');
        // Refresh profile to get updated data
        await fetchProfile();
      } else {
        toast.error(result.message || 'Failed to delete video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Error deleting video. Please try again.');
    }
  }

  const age = useMemo(() => {
    if (!profile?.basic.dob) return null;
    try {
      const birthDate = new Date(profile.basic.dob);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year yet
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      
      return calculatedAge;
    } catch {
      return null;
    }
  }, [profile?.basic.dob]);

  // Check if user should show "Verified" badge based on overallStatus
  const shouldShowVerified = useMemo(() => {
    if (!profile?.basic) return false;
    return profile.basic.overallStatus?.toLowerCase() === 'verified';
  }, [profile?.basic]);

  // Check if user needs verification (isVerified = 0 OR overallStatus = pending)
  const needsVerification = useMemo(() => {
    if (!profile?.basic) return false;
    const isNotVerified = profile.basic.isVerified === false || (profile.basic as any).isVerified === 0;
    const isPending = profile.basic.overallStatus?.toLowerCase() === 'pending';
    return isNotVerified || isPending;
  }, [profile?.basic]);

  const headerPhoto = useMemo(() => {
    if (profile?.photos.length) {
      const primary = profile.photos.find(p => p.isProfileDP);
      const firstPhoto = profile.photos[0];
      const photo = primary || firstPhoto;
      const photoUrl = photo?.photoUrl;
      return getMediaUrl(photoUrl);
    }
    return '';
  }, [profile?.photos]);

  // Check if userId exists before proceeding
  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <User className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">No Profile ID</h2>
            <p className="text-gray-500">No profile ID was provided. Please go back and select a profile.</p>
            <Button onClick={() => navigate('/admin')} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Dashboard
            </Button>

          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Profile Not Available</h2>
            <p className="text-gray-500">Unable to load this profile at moment.</p>
            <Button onClick={() => navigate('/admin')} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Navigation Sections
  const sections = [
    { id: 'personal', label: 'Personal', icon: User, color: 'blue' },
    { id: 'family', label: 'Family Information', icon: Users, color: 'green' },
    { id: 'address', label: 'Address', icon: Home, color: 'orange' },
    { id: 'habits', label: 'Habits & Lifestyle', icon: Heart, color: 'pink' },
    { id: 'astrological', label: 'Astrological', icon: Star, color: 'yellow' },
    { id: 'partner', label: 'Partner Expectations', icon: HeartHandshake, color: 'purple' },
    { id: 'documents', label: 'Documents', icon: FileText, color: 'indigo' },
    { id: 'videos', label: 'Videos', icon: Video, color: 'purple' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Sticky header with Back button */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-purple-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="bg-white border-purple-200 hover:bg-purple-50"
              title="Back to Admin Dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Dashboard
            </Button>
            <h1 className="text-xl sm:text-2xl font-extrabold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              {profile.basic.firstName} {profile.basic.lastName}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Verify Button - Red when needs verification, Green when verified */}
            {needsVerification ? (
              <Button
                onClick={handleVerifyUser}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold"
              >
                <Shield className="w-4 h-4 mr-2" />
                Verify User
              </Button>
            ) : shouldShowVerified ? (
              <Button
                disabled
                className="bg-green-500 text-white font-semibold cursor-not-allowed"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Verified
              </Button>
            ) : null}
            {shouldShowVerified && (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
            {profile?.basic.isPremium && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                <Star className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
            {profile?.basic.isOnline && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1 animate-pulse"></div>
                Online
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top Section: Basic Info + Photo Gallery - UPDATED LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Left Side - Basic Information Card (COLS 1-8) */}
          <div className="lg:col-span-8">
            <Card className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white shadow-2xl border-0 overflow-hidden relative">
              {/* Decorative Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-x-12 translate-y-12"></div>
              
              <CardHeader className="relative z-10 pb-1">
                <div className="text-center">
                  {/* Name and Status - REMOVED DP ICON */}
                  <CardTitle className="text-3xl font-bold text-white drop-shadow-lg mb-1">
                    {profile.basic.firstName} {profile.basic.lastName}
                  </CardTitle>
                  
                  {/* Status Badges - REMOVED DP ICON */}
                  <div className="flex justify-center items-center space-x-2">
                    {shouldShowVerified && (
                      <Badge className="bg-green-500 text-white border-0 text-xs">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {profile.basic.isPremium && (
                      <Badge className="bg-amber-500 text-white border-0 text-xs">
                        <Award className="w-3 h-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                    {profile.basic.isOnline && (
                      <Badge className="bg-blue-500 text-white border-0 text-xs">
                        <div className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></div>
                        Online
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="relative z-10 space-y-2">
                {/* User ID */}
                <div className="text-center">
                  <p className="text-purple-200 text-xs uppercase tracking-wide">User ID</p>
                  <p className="text-white font-mono font-semibold">{profile.basic.userId}</p>
                </div>

                {/* Basic Details Grid - UPDATED FIELDS WITH PAYMENT-BASED BLUR */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Age</p>
                    <p className="text-white font-semibold flex items-center">
                      <Cake className="w-3 h-3 mr-1" />
                      {age || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Gender</p>
                    <p className="text-white font-semibold">{profile.basic.gender || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Height</p>
                    <p className="text-white font-semibold flex items-center">
                      <Scale className="w-3 h-3 mr-1" />
                      {profile.basic.height ? `${profile.basic.height} cm` : '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Weight</p>
                    <p className="text-white font-semibold flex items-center">
                      <Activity className="w-3 h-3 mr-1" />
                      {profile.basic.weight ? `${profile.basic.weight} kg` : '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Religion</p>
                    <p className="text-white font-semibold">{profile.basic.religion || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Caste</p>
                    <p className="text-white font-semibold">{profile.basic.caste || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Tongue</p>
                    <p className="text-white font-semibold">{profile.basic.motherTongue || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Profession</p>
                    <p className="text-white font-semibold flex items-center">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {profile.basic.profession || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Education</p>
                    <p className="text-white font-semibold flex items-center text-sm">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      {profile.personal.education || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Status</p>
                    <p className="text-white font-semibold">{profile.basic.maritalStatus || '—'}</p>
                  </div>
                </div>

                 {/* Contact Info Block */}
                <div className="mt-3 pt-3 border-t border-white/20">
                  <h3 className="text-white font-semibold text-sm mb-3 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Info
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-purple-200 text-xs uppercase">Mobile</p>
                      <p className="text-white font-semibold flex items-center text-sm">
                        <Phone className="w-3 h-3 mr-1" />
                        {profile.basic.mobileNo || '—'}
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-purple-200 text-xs uppercase">Email</p>
                      <p className="text-white font-semibold flex items-center text-sm">
                        <Mail className="w-3 h-3 mr-1" />
                        {profile.basic.email || '—'}
                      </p>
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          </div>

          {/* Right Side - Photo Gallery (COLS 9-12) - ULTRA COMPACT */}
          <div className="lg:col-span-4">
            <Card className="shadow-2xl border-0 bg-white overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-sm">
                    <Users className="w-4 h-4 mr-2" />
                    Photo Gallery
                  </CardTitle>
                  <Badge className="bg-white/20 text-white border-white/30 text-xs">
                    {profile.photos.length} Photos
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3">
                {profile.photos.length > 0 ? (
                  <div className="space-y-1">
                    {/* Main Photo Display - CLICKABLE FOR LARGER VIEW */}
                    <div 
                      className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setShowFullPhoto(true)}
                    >
                      <img 
                        src={getMediaUrl(profile.photos[activePhotoIndex]?.photoUrl)} 
                        alt={`Profile photo ${activePhotoIndex + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/200x200?text=Photo';
                        }}
                      />
                      
                      {/* Zoom Overlay Icon */}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Navigation Arrows */}
                      {profile.photos.length > 1 && (
                        <>
                          <Button
                            variant="ghost"
                            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 rounded-full w-6 h-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
                            }}
                          >
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 rounded-full w-6 h-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePhotoIndex((prev) => (prev + 1) % profile.photos.length);
                            }}
                          >
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      
                      {/* Photo Counter */}
                      <div className="absolute bottom-1 right-1 bg-black/50 text-white px-1 py-0.5 rounded-full text-xs">
                        {activePhotoIndex + 1} / {profile.photos.length}
                      </div>
                      
                      {/* Profile DP Badge */}
                      {profile.photos[activePhotoIndex]?.isProfileDP && (
                        <div className="absolute top-1 left-1">
                          <Badge className="bg-purple-600 text-white text-xs px-1">
                            <Star className="w-2 h-2 mr-1" />
                            DP
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Photo Action Buttons - Approve and Delete */}
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={() => handleApprovePhoto(profile.photos[activePhotoIndex].id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs py-1.5"
                        disabled={profile.photos[activePhotoIndex]?.approvalStatus === 1}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {profile.photos[activePhotoIndex]?.approvalStatus === 1 ? 'Approved' : 'Approve'}
                      </Button>
                      <Button
                        onClick={() => handleDeletePhoto(
                          profile.photos[activePhotoIndex].id,
                          profile.photos[activePhotoIndex].photoFileName
                        )}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs py-1.5"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>

                    {/* Thumbnail Gallery - ULTRA SHORT WITH HORIZONTAL SCROLL */}
                    <div className="overflow-x-auto">
                      <div className="flex gap-0.5 pb-1">
                        {profile.photos.map((photo, index) => (
                          <button
                            key={photo.id}
                            onClick={() => setActivePhotoIndex(index)}
                            className={`flex-shrink-0 w-12 h-12 rounded-sm overflow-hidden border transition-all ${
                              index === activePhotoIndex 
                                ? 'border-purple-500 ring-1 ring-purple-200' 
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <img 
                              src={getMediaUrl(photo.photoUrl)} 
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-xs">No photos uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Payment Plan Selection */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-1 mb-8">
          <div className="bg-white rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">Payment Plan Selection</h2>
            
            {/* Radio Buttons for Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedPlan === '3' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}>
                <input
                  type="radio"
                  name="payment-plan"
                  value="3"
                  checked={selectedPlan === '3'}
                  onChange={() => handlePlanChange('3')}
                  className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="block font-semibold text-gray-800">3 Months Plan</span>
                  <span className="block text-sm text-gray-600">Quarterly</span>
                </div>
                {selectedPlan === '3' && (
                  <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-purple-600" />
                )}
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedPlan === '6' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}>
                <input
                  type="radio"
                  name="payment-plan"
                  value="6"
                  checked={selectedPlan === '6'}
                  onChange={() => handlePlanChange('6')}
                  className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="block font-semibold text-gray-800">6 Months Plan</span>
                  <span className="block text-sm text-gray-600">Half-Yearly</span>
                </div>
                {selectedPlan === '6' && (
                  <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-purple-600" />
                )}
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedPlan === '12' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}>
                <input
                  type="radio"
                  name="payment-plan"
                  value="12"
                  checked={selectedPlan === '12'}
                  onChange={() => handlePlanChange('12')}
                  className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="block font-semibold text-gray-800">12 Months Plan</span>
                  <span className="block text-sm text-gray-600">Annual</span>
                </div>
                {selectedPlan === '12' && (
                  <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-purple-600" />
                )}
              </label>
            </div>

            {/* Profile Validity Date */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                Profile Validity
              </label>
              <input
                type="date"
                value={profileValidity.toISOString().split('T')[0]}
                onChange={(e) => setProfileValidity(new Date(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-600 mt-2">
                Selected Plan: <span className="font-semibold text-purple-600">{selectedPlan} Months</span>
                {' '} | Valid Until: <span className="font-semibold text-purple-600">
                  {profileValidity.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </p>
            </div>

            {/* Update Button */}
            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={handlePaymentUpdate}
                disabled={updatingPayment}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg px-8"
              >
                {updatingPayment ? (
                  <>
                    <Clock className="w-5 h-5 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Update Payment Status
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg p-2">
            <div className="flex flex-wrap gap-2">
              {sections.map((section) => {
                const Icon = section.icon;
                const colorMap = {
                  blue: 'from-blue-500 to-cyan-500',
                  green: 'from-green-500 to-emerald-500',
                  orange: 'from-orange-500 to-amber-500',
                  pink: 'from-pink-500 to-rose-500',
                  yellow: 'from-yellow-500 to-orange-500',
                  indigo: 'from-indigo-500 to-purple-500',
                  purple: 'from-purple-500 to-pink-500'
                };
                const gradientClass = colorMap[section.color as keyof typeof colorMap] || 'from-blue-500 to-cyan-500';
                
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      selectedSection === section.id
                        ? `bg-gradient-to-r ${gradientClass} text-white shadow-md`
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Personal Details Section */}
          {selectedSection === 'personal' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <User className="w-6 h-6 mr-3" />
                  Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* About Me */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800 flex items-center">
                      <User className="w-4 h-4 mr-2 text-blue-500" />
                      About Me
                    </h3>
                    {!editAboutMe ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditAboutMe(true)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveAboutMe}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditAboutMe(false);
                            setAboutMeValue(profile.personal?.aboutMe || '');
                          }}
                          className="text-gray-600 border-gray-300"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  {editAboutMe ? (
                    <textarea
                      value={aboutMeValue}
                      onChange={(e) => setAboutMeValue(e.target.value)}
                      className="w-full bg-blue-50 p-4 rounded-lg border border-blue-200 focus:border-blue-400 focus:outline-none min-h-[120px] text-gray-700 leading-relaxed"
                      placeholder="Tell us about yourself..."
                    />
                  ) : (
                    <p className="text-gray-600 bg-blue-50 p-4 rounded-lg leading-relaxed">
                      {profile.personal.aboutMe || 'No information provided'}
                    </p>
                  )}
                </div>

                {/* Physical Attributes */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Body Type</h4>
                    <p className="text-gray-800">{profile.personal.bodyType || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Complexion</h4>
                    <p className="text-gray-800">{profile.personal.complexion || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Physical Status</h4>
                    <p className="text-gray-800">{profile.personal.physicalStatus || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Blood Group</h4>
                    <p className="text-gray-800">{profile.personal.bloodGroup || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Height</h4>
                    <p className="text-gray-800">{profile.basic.height ? `${profile.basic.height} cm` : '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Weight</h4>
                    <p className="text-gray-800">{profile.basic.weight ? `${profile.basic.weight} kg` : '—'}</p>
                  </div>
                </div>

                {/* Habits & Lifestyle */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-pink-500" />
                    Habits & Lifestyle
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-1">Diet</h4>
                      <p className="text-green-800">{profile.personal.diet || '—'}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Smoking</h4>
                      <p className="text-orange-800">{profile.personal.smoke || '—'}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-1">Drinking</h4>
                      <p className="text-blue-800">{profile.personal.drink || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Hobbies & Interests */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                    Hobbies & Interests
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Hobbies</h4>
                      <p className="text-gray-800">{profile.personal.hobbies || '—'}</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Interests</h4>
                      <p className="text-gray-800">{profile.personal.interests || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Education & Work */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Briefcase className="w-4 h-4 mr-2 text-green-500" />
                    Education & Work
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-1">Education</h4>
                      <p className="text-gray-800">{profile.personal.education || '—'}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-1">Work Experience</h4>
                      <p className="text-gray-800">{profile.personal.workExperience || '—'}</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-700 mb-1">Annual Income</h4>
                      <p className="text-gray-800">{profile.personal.annualIncome || '—'}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-1">Employed In</h4>
                      <p className="text-gray-800">{profile.personal.employedIn || '—'}</p>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h4 className="font-medium text-indigo-700 mb-1">Occupation</h4>
                      <p className="text-gray-800">{profile.personal.occupation || '—'}</p>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-lg">
                      <h4 className="font-medium text-cyan-700 mb-1">Job Location</h4>
                      <p className="text-gray-800">{profile.personal.jobLocation || '—'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Family Information Section */}
          {selectedSection === 'family' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Users className="w-6 h-6 mr-3" />
                  Family Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Parents Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Home className="w-4 h-4 mr-2 text-green-500" />
                    Parents Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-700 mb-2">Father's Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profile.family.fatherName || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Occupation:</span>
                            <span className="font-medium">{profile.family.fatherOccupation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Education:</span>
                            <span className="font-medium">{profile.family.fatherEducation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <Badge className={profile.family.fatherAlive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                              {profile.family.fatherAlive ? "Living" : "Not Living"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="bg-pink-50 p-4 rounded-lg">
                        <h4 className="font-medium text-pink-700 mb-2">Mother's Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profile.family.motherName || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Occupation:</span>
                            <span className="font-medium">{profile.family.motherOccupation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Education:</span>
                            <span className="font-medium">{profile.family.motherEducation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <Badge className={profile.family.motherAlive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                              {profile.family.motherAlive ? "Living" : "Not Living"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Siblings Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Siblings Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-blue-800 text-sm">Brothers</h4>
                      <p className="text-2xl font-bold text-blue-600">{profile.family.brothersCount || 0}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Married: {profile.family.marriedBrothers || 0}
                      </p>
                    </div>
                    <div className="bg-pink-100 border border-pink-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-pink-800 text-sm">Sisters</h4>
                      <p className="text-2xl font-bold text-pink-600">{profile.family.sistersCount || 0}</p>
                      <p className="text-xs text-pink-600 mt-1">
                        Married: {profile.family.marriedSisters || 0}
                      </p>
                    </div>
                    <div className="bg-purple-100 border border-purple-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-purple-800 text-sm">Unmarried Bros</h4>
                      <p className="text-2xl font-bold text-purple-600">{profile.family.unmarriedBrothers || 0}</p>
                    </div>
                    <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-amber-800 text-sm">Unmarried Sisters</h4>
                      <p className="text-2xl font-bold text-amber-600">{profile.family.unmarriedSisters || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Family Background */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-purple-500" />
                    Family Background
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Type</h4>
                      <p className="text-gray-800">{profile.family.familyType || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Status</h4>
                      <p className="text-gray-800">{profile.family.familyStatus || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Values</h4>
                      <p className="text-gray-800">{profile.family.familyValues || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Income</h4>
                      <p className="text-gray-800">{profile.family.familyIncome || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Native Place</h4>
                      <p className="text-gray-800">{profile.family.nativePlace || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Living In</h4>
                      <p className="text-gray-800">{profile.family.familyLivingIn || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* About Family */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800 flex items-center">
                      <Heart className="w-4 h-4 mr-2 text-pink-500" />
                      About Family
                    </h3>
                    {!editAboutFamily ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditAboutFamily(true)}
                        className="text-pink-600 border-pink-300 hover:bg-pink-50"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveAboutFamily}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditAboutFamily(false);
                            setAboutFamilyValue(profile.family?.aboutFamily || '');
                          }}
                          className="text-gray-600 border-gray-300"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  {editAboutFamily ? (
                    <textarea
                      value={aboutFamilyValue}
                      onChange={(e) => setAboutFamilyValue(e.target.value)}
                      className="w-full bg-green-50 p-4 rounded-lg border border-green-200 focus:border-green-400 focus:outline-none min-h-[120px] text-gray-700 leading-relaxed"
                      placeholder="Tell us about your family..."
                    />
                  ) : (
                    <p className="text-gray-600 bg-green-50 p-4 rounded-lg leading-relaxed">
                      {profile.family.aboutFamily || 'No information provided'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Address Details Section */}
          {selectedSection === 'address' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Home className="w-6 h-6 mr-3" />
                  Address Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Address */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      Current Address
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Address Line</h4>
                        <p className="text-gray-800">
                          {[profile.address.buildingNo, profile.address.buildingName, profile.address.areaName, profile.address.street]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">City</h4>
                          <p className="text-gray-800">{profile.address.city || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">State</h4>
                          <p className="text-gray-800">{profile.address.state || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">Pincode</h4>
                          <p className="text-gray-800">{profile.address.pincode || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">Country</h4>
                          <p className="text-gray-800">{profile.address.country || '—'}</p>
                        </div>
                      </div>
                      {profile.address.landmark && (
                        <div className="bg-orange-50 p-3 rounded-lg">
                          <h4 className="font-medium text-orange-700 text-sm">Landmark</h4>
                          <p className="text-gray-800">{profile.address.landmark}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Address Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                      <Building className="w-4 h-4 mr-2 text-amber-500" />
                      Additional Information
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Residence Type</h4>
                        <p className="text-gray-800">{profile.address.residence || '—'}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Address Type</h4>
                        <p className="text-gray-800">{profile.address.addressType || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Habits & Lifestyle Section */}
          {selectedSection === 'habits' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Heart className="w-6 h-6 mr-3" />
                  Habits & Lifestyle
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Lifestyle Habits */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Activity className="w-4 h-4 mr-2 text-pink-500" />
                    Lifestyle Habits
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-green-700 mb-2">Diet</h4>
                      <p className="text-lg font-semibold text-green-800">{profile.personal.diet || '—'}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-orange-700 mb-2">Smoking</h4>
                      <p className="text-lg font-semibold text-orange-800">{profile.personal.smoke || '—'}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-blue-700 mb-2">Drinking</h4>
                      <p className="text-lg font-semibold text-blue-800">{profile.personal.drink || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Physical Attributes */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-4 h-4 mr-2 text-purple-500" />
                    Physical Attributes
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Body Type</h4>
                      <p className="text-gray-800">{profile.personal.bodyType || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Complexion</h4>
                      <p className="text-gray-800">{profile.personal.complexion || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Physical Status</h4>
                      <p className="text-gray-800">{profile.personal.physicalStatus || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Blood Group</h4>
                      <p className="text-gray-800">{profile.personal.bloodGroup || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Height</h4>
                      <p className="text-gray-800">{profile.basic.height ? `${profile.basic.height} cm` : '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Weight</h4>
                      <p className="text-gray-800">{profile.basic.weight ? `${profile.basic.weight} kg` : '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Hobbies & Interests */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                    Hobbies & Interests
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Hobbies</h4>
                      <p className="text-gray-800">{profile.personal.hobbies || '—'}</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Interests</h4>
                      <p className="text-gray-800">{profile.personal.interests || '—'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Astrological Details Section */}
          {selectedSection === 'astrological' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Star className="w-6 h-6 mr-3" />
                  Astrological Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Birth Details */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
                    Birth Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Birth Time</h4>
                      <p className="text-gray-800">{profile.personal.birthTime || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Birth Place</h4>
                      <p className="text-gray-800">{profile.personal.birthPlace || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Rashi</h4>
                      <p className="text-gray-800">{profile.personal.rashi || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Nakshatra</h4>
                      <p className="text-gray-800">{profile.personal.nakshatra || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Astrological Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-orange-500" />
                    Astrological Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Sub Caste</h4>
                      <p className="text-gray-800">{profile.personal.subCaste || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Gotra</h4>
                      <p className="text-gray-800">{profile.personal.gotra || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Manglik</h4>
                      <p className="text-gray-800">{profile.personal.manglik || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Charan</h4>
                      <p className="text-gray-800">{profile.personal.charan || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Gan</h4>
                      <p className="text-gray-800">{profile.personal.gan || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Nadi</h4>
                      <p className="text-gray-800">{profile.personal.nadi || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Horoscope Match */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <HeartHandshake className="w-4 h-4 mr-2 text-pink-500" />
                    Horoscope Preferences
                  </h3>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <h4 className="font-medium text-pink-700 mb-1">Horoscope Match</h4>
                    <p className="text-gray-800">{profile.personal.horoscopeMatch || '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Section */}
          {selectedSection === 'documents' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <FileText className="w-6 h-6 mr-3" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {profile.documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.documents.map((doc) => (
                      <div key={doc.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-800">{doc.documentType}</h4>
                              {doc.status === 'Approved' && (
                                <Badge className="bg-green-100 text-green-800 border-green-300">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{doc.documentFileName}</p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{(doc.documentSize / (1024 * 1024)).toFixed(1)} MB</span>
                              <span>•</span>
                              <span>{doc.documentFormat.toUpperCase()}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                // Open document in new tab
                                window.open(getMediaUrl(doc.documentUrl), '_blank');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => handleApproveDocument(doc.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                            disabled={doc.status === 'Approved'}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {doc.status === 'Approved' ? 'Approved' : 'Approve'}
                          </Button>
                          <Button
                            onClick={() => handleDeleteDocument(doc.id, doc.documentFileName)}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No documents uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Videos Section */}
          {selectedSection === 'videos' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Video className="w-6 h-6 mr-3" />
                  Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {profile.videos && profile.videos.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.videos.map((video) => (
                      <div key={video.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-800">{video.videoTitle || video.videoFileName}</h4>
                              {video.status === 'Approved' && (
                                <Badge className="bg-green-100 text-green-800 border-green-300">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{video.videoFileName}</p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{(video.videoSize / (1024 * 1024)).toFixed(1)} MB</span>
                              <span>•</span>
                              <span>{video.videoFormat.toUpperCase()}</span>
                              {video.videoDuration && (
                                <>
                                  <span>•</span>
                                  <span>{Math.floor(video.videoDuration / 60)}:{String(video.videoDuration % 60).padStart(2, '0')}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                // Open video in new tab
                                window.open(getMediaUrl(video.videoUrl), '_blank');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => handleApproveVideo(video.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                            disabled={video.status === 'Approved'}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {video.status === 'Approved' ? 'Approved' : 'Approve'}
                          </Button>
                          <Button
                            onClick={() => handleDeleteVideo(video.id, video.videoFileName)}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No videos uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Partner Expectations Section */}
          {selectedSection === 'partner' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <HeartHandshake className="w-6 h-6 mr-3" />
                  Partner Expectations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Basic Preferences */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-4 h-4 mr-2 text-purple-500" />
                    Basic Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Age Range</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.ageMin && profile.partnerExpectations.ageMax ? 
                          `${profile.partnerExpectations.ageMin} - ${profile.partnerExpectations.ageMax} years` : 
                          '—'}
                      </p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Height Range</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.heightMin && profile.partnerExpectations.heightMax ? 
                          `${profile.partnerExpectations.heightMin} - ${profile.partnerExpectations.heightMax} cm` : 
                          '—'}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-2">Marital Status</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.maritalStatus || '—'}
                      </p>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h4 className="font-medium text-indigo-700 mb-2">Religion</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.religion || '—'}
                      </p>
                    </div>
                    <div className="bg-violet-50 p-4 rounded-lg">
                      <h4 className="font-medium text-violet-700 mb-2">Caste</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.caste || '—'}
                      </p>
                    </div>
                    <div className="bg-fuchsia-50 p-4 rounded-lg">
                      <h4 className="font-medium text-fuchsia-700 mb-2">Mother Tongue</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.motherTongue || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Professional Preferences */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                    Professional Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-2">Education</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.education || '—'}
                      </p>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-lg">
                      <h4 className="font-medium text-cyan-700 mb-2">Profession</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.profession || '—'}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <h4 className="font-medium text-teal-700 mb-2">Annual Income</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.annualIncome || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location Preferences */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-green-500" />
                    Location Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-2">Location</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.location || '—'}
                      </p>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <h4 className="font-medium text-emerald-700 mb-2">Country</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.country || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Lifestyle Preferences */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-red-500" />
                    Lifestyle Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-700 mb-2">Diet</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.diet || '—'}
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-2">Smoking</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.smoke || '—'}
                      </p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-700 mb-2">Drinking</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.drink || '—'}
                      </p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-2">Manglik</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.manglik || '—'}
                      </p>
                    </div>
                    <div className="bg-lime-50 p-4 rounded-lg">
                      <h4 className="font-medium text-lime-700 mb-2">Horoscope Match</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.horoscopeMatch || '—'}
                      </p>
                    </div>
                    <div className="bg-rose-50 p-4 rounded-lg">
                      <h4 className="font-medium text-rose-700 mb-2">Physical Status</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.physicalStatus || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Family Preferences */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-indigo-500" />
                    Family Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h4 className="font-medium text-indigo-700 mb-2">Family Values</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.familyValues || '—'}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Family Type</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.familyType || '—'}
                      </p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Family Status</h4>
                      <p className="text-gray-800">
                        {profile.partnerExpectations.familyStatus || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* About Partner */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800 flex items-center">
                      <HeartHandshake className="w-4 h-4 mr-2 text-pink-500" />
                      About Partner
                    </h3>
                    {!editAboutPartner ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditAboutPartner(true)}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveAboutPartner}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditAboutPartner(false);
                            setAboutPartnerValue(profile.partnerExpectations?.aboutPartner || '');
                          }}
                          className="text-gray-600 border-gray-300"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  {editAboutPartner ? (
                    <textarea
                      value={aboutPartnerValue}
                      onChange={(e) => setAboutPartnerValue(e.target.value)}
                      className="w-full bg-gradient-to-r from-pink-50 to-purple-50 p-4 rounded-lg border border-purple-200 focus:border-purple-400 focus:outline-none min-h-[120px] text-gray-700 leading-relaxed"
                      placeholder="Describe your ideal partner..."
                    />
                  ) : (
                    <p className="text-gray-600 bg-gradient-to-r from-pink-50 to-purple-50 p-4 rounded-lg leading-relaxed">
                      {profile.partnerExpectations.aboutPartner || 'No information provided'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Chat Modal (user-initiated) */}
      {chatOpen && chatConversationWith && (
        <ChatModal
          /**
           * NOTE:
           * ChatModal expects `otherUserId` and `otherDisplayName` (optional) and `visible` props.
           * Provide the name that the parent already has so the modal shows it immediately
           * instead of attempting to fetch (which can fail and result in "Loading..." being shown).
           */
          otherUserId={chatConversationWith.id}
          otherDisplayName={chatConversationWith.name}
          visible={true}
          onClose={() => {
            setChatOpen(false);
            setChatConversationWith(null);
          }}
        />
      )}

      {/* Full Screen Photo Modal - NEW */}
      {showFullPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullPhoto(false)}
        >
          <div className="relative max-w-6xl max-h-full">
            <Button
              variant="ghost"
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
              onClick={() => setShowFullPhoto(false)}
            >
              <X className="w-6 h-6" />
            </Button>
            
            <img 
              src={getMediaUrl(profile.photos[activePhotoIndex]?.photoUrl)} 
              alt={`Photo ${activePhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            
            {/* Navigation Buttons in Fullscreen */}
            {profile.photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
                  }}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  variant="ghost"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIndex((prev) => (prev + 1) % profile.photos.length);
                  }}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}
            
            {/* Photo Counter in Fullscreen */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full">
              <p className="text-sm font-medium">
                {activePhotoIndex + 1} / {profile.photos.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer with additional actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="text-center sm:text-left mb-4 sm:mb-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Report Profile</h3>
              <p className="text-gray-600 text-sm">If you find any issues with this profile</p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                <AlertCircle className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
              <Button variant="outline" className="border-gray-200 text-gray-600 hover:bg-gray-50">
                <Share2 className="w-4 h-4 mr-2" />
                Share Profile
              </Button>
            </div>
          </div> 
        </div>
      </div>
    </div>
  );
}